/**
 * Uma linha da tela de preferências de aviso (parte D1; peça D do desenho §5.1-§5.2).
 *
 * Salva NA HORA, por linha ("Salvando… / Salvo / Não salvou. Tentar de novo"): são 12
 * linhas, e um botão "Salvar" esquecido no fim da página perderia tudo. Pílulas num
 * RadioGroup do Radix (teclado e `aria-checked` de graça) em GRID — nunca `flex-wrap`
 * (a cicatriz: a 4ª pílula descia no desktop a 1 100 px e ninguém via). A pílula
 * abaixo do piso é `aria-disabled` (não `disabled`): continua focável e o leitor de
 * tela lê o motivo pelo `aria-describedby`.
 *
 * O rótulo da categoria aparece uma vez só no DOM visível; quem usa leitor de tela o
 * ouve pelo `aria-label` do grupo de pílulas ("<rótulo>: como avisar").
 */
import { useCallback, useEffect, useId, useRef, useState } from "react";
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import { Info, Lock, Volume2 } from "lucide-react";
import { useDefinirNivelDeAviso } from "@/hooks/usePreferenciasDeAviso";
import { cn } from "@/lib/utils";
import type {
  AdaptadorDePreferencias,
  CategoriaDeAviso,
  NivelDeAviso,
} from "@/services/preferenciasDeAvisoService";
import { apresentacaoDaCategoria, type ApresentacaoDaCategoria } from "./categoriasDeAviso";
import { GRADES, type DisposicaoDaLinha } from "./LinhaDeAviso.constants";

/** Textos aprovados (desenho §1) — não mudam sem passar pelo Augusto. */
const ROTULO: Record<NivelDeAviso, string> = {
  som: "Aviso + som",
  aviso: "Aviso sem som",
  sino: "Só no sino",
  nada: "Não receber",
};
const COLUNAS: NivelDeAviso[] = ["som", "aviso", "sino", "nada"];
const ALTURA: Record<NivelDeAviso, number> = { nada: 0, sino: 1, aviso: 2, som: 3 };

type Estado = "ocioso" | "salvando" | "salvo" | "erro";

interface Props {
  categoria: CategoriaDeAviso;
  /** A API onde a linha salva (padrão: a interna; o portal injeta a dele). */
  adaptador?: AdaptadorDePreferencias;
  /** A cara das categorias (padrão: as internas). */
  categorias?: Record<string, ApresentacaoDaCategoria>;
  disposicao?: DisposicaoDaLinha;
}

export function LinhaDeAviso({ categoria, adaptador, categorias, disposicao = "lado" }: Props) {
  const definir = useDefinirNivelDeAviso(categoria.chave, adaptador);
  const [nivel, setNivel] = useState<NivelDeAviso>(categoria.nivel);
  const [estado, setEstado] = useState<Estado>("ocioso");
  const [tentado, setTentado] = useState<NivelDeAviso | null>(null);
  const [piscando, setPiscando] = useState(false);
  // Espelho do "salvando" para o efeito do `categoria.nivel` ler sem depender do `estado`
  // (se dependesse, o efeito rodaria a cada mudança de estado e desfaria a escolha otimista).
  const salvandoRef = useRef(false);
  // O último valor CONFIRMADO pelo servidor (o do cache), sempre o da renderização mais nova.
  // É para ele que a pílula volta quando o salvamento falha: um instantâneo local do que
  // estava na tela seria uma escolha otimista que o servidor nunca confirmou (dois cliques
  // seguidos, o 2º falha, e a pílula voltaria ao 1º, que o servidor não tem).
  const confirmadoRef = useRef(categoria.nivel);
  confirmadoRef.current = categoria.nivel;
  const timerDoPiscar = useRef<number | undefined>(undefined);
  const idDaLegenda = useId();
  const { descricao, Icone, rotuloDoSino } = apresentacaoDaCategoria(categoria.chave, categorias);
  const grade = GRADES[disposicao];

  const irPara = useCallback((novo: Estado) => {
    salvandoRef.current = novo === "salvando";
    setEstado(novo);
  }, []);

  // O servidor mudou a linha (ex.: "Restaurar padrões"): a tela acompanha — menos no meio de um salvamento.
  useEffect(() => {
    if (!salvandoRef.current) setNivel(categoria.nivel);
  }, [categoria.nivel]);

  useEffect(() => {
    if (estado !== "salvo") return;
    const t = window.setTimeout(() => irPara("ocioso"), 2000);
    return () => window.clearTimeout(t);
  }, [estado, irPara]);

  // Desmontou no meio do piscar (ex.: a lista recarregou): o temporizador não sobrevive à linha.
  useEffect(() => () => window.clearTimeout(timerDoPiscar.current), []);

  const travado = (n: NivelDeAviso) =>
    categoria.piso !== null && ALTURA[n] < ALTURA[categoria.piso];

  const salvar = (novo: NivelDeAviso) => {
    setNivel(novo);
    irPara("salvando");
    setTentado(novo);
    definir.mutate(novo, {
      onSuccess: () => irPara("salvo"),
      onError: () => {
        setNivel(confirmadoRef.current);
        irPara("erro");
      },
    });
  };

  const piscarALegenda = () => {
    window.clearTimeout(timerDoPiscar.current);
    setPiscando(true);
    timerDoPiscar.current = window.setTimeout(() => setPiscando(false), 300);
  };

  const escolher = (valor: string) => {
    const novo = valor as NivelDeAviso;
    if (travado(novo)) {
      // Clique na pílula travada: nada muda e a legenda pisca, mostrando onde está o motivo.
      piscarALegenda();
      return;
    }
    if (novo !== nivel) salvar(novo);
  };

  return (
    <div className={grade.linha}>
      <div className="flex items-start gap-3">
        <span
          aria-hidden="true"
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary [&_svg]:h-4 [&_svg]:w-4"
        >
          <Icone />
        </span>
        <div className="min-w-0">
          <p className="text-sm font-medium">{categoria.rotulo}</p>
          <p className="text-xs text-muted-foreground">{descricao}</p>
          {categoria.motivo_do_piso && (
            <p
              id={idDaLegenda}
              className={cn(
                "mt-1 flex items-start gap-1 text-xs text-muted-foreground",
                piscando && "animate-fade-phase"
              )}
            >
              <Info aria-hidden="true" className="mt-0.5 h-3 w-3 shrink-0" />
              {categoria.motivo_do_piso}
            </p>
          )}
          <p
            role="status"
            className={cn(
              "mt-1 text-xs",
              estado === "salvando" && "text-muted-foreground",
              estado === "salvo" && "text-green-700 dark:text-green-400",
              estado === "erro" && "text-destructive"
            )}
          >
            {estado === "salvando" && "Salvando…"}
            {estado === "salvo" && "Salvo"}
            {estado === "erro" && (
              <>
                Não salvou.{" "}
                <button
                  type="button"
                  className="inline-flex min-h-[44px] items-center underline underline-offset-2 sm:min-h-0"
                  onClick={() => tentado && salvar(tentado)}
                >
                  Tentar de novo
                </button>
              </>
            )}
          </p>
        </div>
      </div>
      <RadioGroupPrimitive.Root
        value={nivel}
        onValueChange={escolher}
        aria-label={`${categoria.rotulo}: como avisar`}
        className={grade.pilulas}
      >
        {COLUNAS.map((n) =>
          categoria.niveis_permitidos.includes(n) ? (
            <RadioGroupPrimitive.Item
              key={n}
              value={n}
              aria-disabled={travado(n) || undefined}
              aria-describedby={travado(n) && categoria.motivo_do_piso ? idDaLegenda : undefined}
              className={cn(
                "flex min-h-[44px] items-center justify-center gap-1 whitespace-normal rounded-md border px-2 text-center text-xs leading-tight sm:text-sm",
                grade.alturaDaPilula,
                "border-input bg-background text-muted-foreground hover:bg-accent hover:text-accent-foreground",
                "data-[state=checked]:border-primary data-[state=checked]:bg-primary data-[state=checked]:font-medium data-[state=checked]:text-primary-foreground",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                travado(n) && "cursor-not-allowed opacity-60"
              )}
            >
              {travado(n) && <Lock aria-hidden="true" className="h-3 w-3 shrink-0" />}
              {n === "som" && <Volume2 aria-hidden="true" className="h-3 w-3 shrink-0" />}
              {n === "sino" ? (rotuloDoSino ?? ROTULO.sino) : ROTULO[n]}
            </RadioGroupPrimitive.Item>
          ) : (
            // Nas conversas a 4ª coluna fica vazia: no desktop as colunas seguem alinhadas.
            <span key={n} aria-hidden="true" className={grade.celulaVazia} />
          )
        )}
      </RadioGroupPrimitive.Root>
    </div>
  );
}
